import Phaser from 'phaser';
import { GameSystem } from '../systems/types';
import type { MainSceneShape } from '../types/scene';
import { gameBridge } from '../bridge';
import { V2Autotile, V2State, V2Tileset } from '../../lib/mapV2';
import { logger } from '../../lib/logger';
import { setBubbleMembers as uiSetBubbleMembers } from '../ui/bubbles';
import { ensureRecenterUi, updateRecenterUiVisibility } from '../camera/recenterUi';
import { setCollisionVisible, updateCollisionOverlay } from '../collision/overlay';
import { setAssetPreview } from '../editor/editorAssets';
import { fetchAndApplyServerLayers } from '../map/serverSync';
import { loadVisibleChunks, applyChunkUpdates } from '../map/chunks';
import type { ChunkLayerName } from '../map/chunks';
import { registerTileset } from '../map/tilesets';
import { EditorService } from '../../services/EditorService';
import { EditorIntegration } from '../editor/integration';
import { AutotileGrid, AutotileRenderer, AutotileTextureLoader } from '../autotile';
import { avatarRegistry } from '../avatarRegistry';
import { useMapStore } from '../../state/mapStore';
import {
  PlayerManager,
  RemotePlayersManager,
  CameraController,
  CollisionManager,
  TileManager,
  UIManager,
  NameLabelManager,
  SceneInitializer,
  ObjectManager,
} from './main';
import type { ObjectsUpdatedPayload } from './main';

export class MainScene extends Phaser.Scene implements MainSceneShape {
  private playerManager!: PlayerManager;
  private remotePlayersManager!: RemotePlayersManager;
  private cameraController!: CameraController;
  private collisionManager!: CollisionManager;
  private tileManager!: TileManager;
  private uiManager!: UIManager;
  private nameLabelManager!: NameLabelManager;
  private objectManager!: ObjectManager;

  // Tilemap state: public because helper functions in game/map/* access it.
  public mapRef?: Phaser.Tilemaps.Tilemap;
  public editorGround?: Phaser.Tilemaps.TilemapLayer;
  public wallsLayer?: Phaser.Tilemaps.TilemapLayer;
  public collisionLayer?: Phaser.Tilemaps.TilemapLayer;
  public dynamicTilesets: Map<string, Phaser.Tilemaps.Tileset> = new Map();
  public bubbleOutlines: Map<string, Phaser.GameObjects.Graphics> = new Map();
  public v2?: { state: V2State; firstGids: number[]; chunkSize: number };
  public loadedChunks: Set<string> = new Set();
  private systems: GameSystem[] = [];
  private editorMode = false;
  private editorMapObjectsSnapshot: import('../../services/EditorTypes').MapObjectRecord[] | null = null;
  public autotileGrid?: AutotileGrid;
  public autotileRenderer?: AutotileRenderer;
  private autotileTextureLoader?: AutotileTextureLoader;
  private _lastCamSig: string | null = null;

  // Helper-function boundary: optional fields, sometimes set by helper
  // functions (game/{ui,camera,collision,editor}) rather than by the scene
  // itself. In current OSS code, manager classes run in parallel with their
  // own state; this interface is the library boundary for external subclasses.
  public hero?: Phaser.Physics.Arcade.Sprite;
  public nameLabels?: Map<string, Phaser.GameObjects.Container>;
  public heroNameLabel?: Phaser.GameObjects.Container;
  public remotes?: Map<string, Phaser.GameObjects.Sprite>;
  public recenterUi?: Phaser.GameObjects.Container;
  public manualCameraActive?: boolean;
  public _lastCameraManualNotified?: boolean;
  public ghostSprite?: Phaser.GameObjects.Image;
  public ghostTextureKey?: string;
  public _ghostDataUrl?: string;
  public staticColliders?: Phaser.Physics.Arcade.StaticGroup;
  public heroVsStaticCollider?: Phaser.Physics.Arcade.Collider;
  public heroVsTilesCollider?: Phaser.Physics.Arcade.Collider;
  public labelLayer?: Phaser.GameObjects.Layer | null;
  public labelCamera?: Phaser.Cameras.Scene2D.Camera | null;

  public currentMapId: string = (() => {
    try {
      return useMapStore.getState().currentMapId || '';
    } catch {
      return '';
    }
  })();
  public currentMapName: string = (() => {
    try {
      return useMapStore.getState().currentMapName || 'office';
    } catch {
      return 'office';
    }
  })();
  public terrainTilesetSources: Map<string, string> = new Map();
  public collisionVisible: boolean = false;
  public collisionOverlay?: Phaser.GameObjects.Graphics;
  public editorSprites: Map<string, Phaser.GameObjects.Image> = new Map();
  public pendingTextures: Set<string> = new Set();
  private editorIntegration?: EditorIntegration;

  constructor() {
    super('Main');
  }

  create() {
    // Re-read currentMapId/Name on every create() since Phaser reuses scene instances
    this.currentMapId = useMapStore.getState().currentMapId || '';
    this.currentMapName = useMapStore.getState().currentMapName || 'office';

    // Clear stale chunk cache from previous map (Phaser reuses scene instances)
    this.loadedChunks.clear();
    this._lastCamSig = null;

    const mapData = SceneInitializer.initializeMap(this);
    Object.assign(this, mapData);

    const cameraData = SceneInitializer.initializeCamera(this, this.mapRef!);
    this.labelLayer = cameraData.labelLayer;
    this.labelCamera = cameraData.labelCamera;

    this.autotileGrid = new AutotileGrid();
    this.autotileRenderer = new AutotileRenderer(this, this.autotileGrid, this.mapRef?.tileWidth ?? 16);
    this.autotileTextureLoader = new AutotileTextureLoader(this, this.currentMapId, this.autotileRenderer);
    this.registerAutotileDefinitions(this.v2?.state.autotilePalette ?? []);

    void this.loadVisibleChunks('ground');
    void this.loadVisibleChunks('walls');
    void this.loadVisibleChunks('collision');
    void this.loadVisibleChunks('walls_auto');

    this.initializeManagers();
    this.setupInputHandlers();
    this.setupEditorSubscriptions();
    this.loadServerData();
    this.setupCleanup();

    // Initialize EditorIntegration with tilemap references for local paint application
    this.editorIntegration = new EditorIntegration(this, this.mapRef?.tileWidth ?? 16, {
      tileManager: this.tileManager,
      autotileGrid: this.autotileGrid,
      autotileRenderer: this.autotileRenderer,
      collisionManager: this.collisionManager,
    });

    // Check for pending autotile registrations (deferred from bridge before scene was ready)
    const pending = gameBridge._pendingAutotiles;
    if (pending) {
      this.registerAutotileDefinitions(pending);
      delete gameBridge._pendingAutotiles;
    }
  }

  private initializeManagers() {
    const initialPos = window.initialPlayerPosition || { x: 80, y: 120 };

    const avatarId = localStorage.getItem('avatarId') || 'default-characters:business_man';
    this.playerManager = new PlayerManager({
      scene: this,
      physics: this.physics,
      anims: this.anims,
      mapRef: this.mapRef!,
      initialPos,
      avatarId,
    });
    // A stored custom avatar has no preloaded texture/manifest at boot (it is
    // not part of the default packs), so the hero would render the default.
    // Resolve + swap it in asynchronously so the hero shows its own custom look.
    if (avatarId.startsWith('custom:') && !this.textures.exists(avatarRegistry.getTextureKey(avatarId))) {
      this.changeHeroAvatar(avatarId);
    }
    this.nameLabelManager = new NameLabelManager(this);
    this.nameLabelManager.createHeroLabel('Loading...', initialPos.x, initialPos.y);
    this.remotePlayersManager = new RemotePlayersManager(this);
    this.cameraController = new CameraController({
      scene: this,
      camera: this.cameras.main,
      hero: this.playerManager.getHero(),
      editorMode: this.editorMode,
    });
    this.cameraController.init(this.input);
    this.cameras.main.startFollow(this.playerManager.getHero(), true, 0.1, 0.1);
    ensureRecenterUi(this);
    updateRecenterUiVisibility(this);
    this.collisionManager = new CollisionManager({
      scene: this,
      hero: this.playerManager.getHero(),
      collisionLayer: this.collisionLayer,
      mapRef: this.mapRef!,
    });
    this.collisionManager.ensureCollisionCollider();
    this.tileManager = new TileManager({
      scene: this,
      mapRef: this.mapRef!,
      v2: this.v2,
      editorGround: this.editorGround,
      wallsLayer: this.wallsLayer,
      collisionLayer: this.collisionLayer,
      dynamicTilesets: this.dynamicTilesets,
    });
    this.tileManager.updateBackgrounds();
    this.tileManager.updateGrid();
    this.uiManager = new UIManager({ scene: this, getEditorMode: () => this.editorMode });
    this.uiManager.init();
    this.objectManager = new ObjectManager({ scene: this });
  }

  private setupInputHandlers() {
    // Register SPACE key to prevent default browser behavior (scrolling)
    this.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

    // ==================================================
    // SPACE WORK - INTERACAO POR PROXIMIDADE
    // ==================================================

    const spaceWorkInteractKey =
      this.input.keyboard!.addKey(
        Phaser.Input.Keyboard.KeyCodes.E
      );

    const spaceWorkPromptBg =
      this.add.rectangle(
        this.scale.width / 2,
        this.scale.height - 105,
        250,
        38,
        0x0b1017,
        0.96,
      )
      .setStrokeStyle(
        1,
        0xc8203b,
        1,
      )
      .setScrollFactor(0)
      .setDepth(99998)
      .setVisible(false);

    const spaceWorkPromptText =
      this.add.text(
        this.scale.width / 2,
        this.scale.height - 105,
        "",
        {
          fontFamily:
            "Arial, sans-serif",
          fontSize:
            "13px",
          color:
            "#ffffff",
          fontStyle:
            "bold",
        },
      )
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(99999)
      .setVisible(false);

    type SpaceWorkPanelId =
      "commercial"
      | "sdr"
      | "executive";

    type SpaceWorkInteraction = {
      id: SpaceWorkPanelId;
      x: number;
      y: number;
      label: string;
    };

    const spaceWorkInteractions:
      SpaceWorkInteraction[] = [
        {
          id: "commercial",
          x: 276,
          y: 178,
          label: "Painel Comercial",
        },
        {
          id: "sdr",
          x: 276,
          y: 418,
          label: "Painel SDR",
        },
        {
          id: "executive",
          x: 1204,
          y: 278,
          label: "Visao Geral",
        },
      ];

    let spaceWorkActiveInteraction:
      SpaceWorkInteraction | null = null;

    type SpaceWorkDesk = {
      id: string;
      seatX: number;
      seatY: number;
      standX: number;
      standY: number;
    };

    const spaceWorkDesks: SpaceWorkDesk[] = [
      { id: "commercial-1", seatX: 112, seatY: 184, standX: 112, standY: 222 },
      { id: "commercial-2", seatX: 216, seatY: 184, standX: 216, standY: 222 },
      { id: "commercial-3", seatX: 320, seatY: 184, standX: 320, standY: 222 },
      { id: "commercial-4", seatX: 164, seatY: 264, standX: 164, standY: 302 },
      { id: "commercial-5", seatX: 268, seatY: 264, standX: 268, standY: 302 },

      { id: "sdr-1", seatX: 112, seatY: 432, standX: 112, standY: 470 },
      { id: "sdr-2", seatX: 216, seatY: 432, standX: 216, standY: 470 },
      { id: "sdr-3", seatX: 320, seatY: 432, standX: 320, standY: 470 },
      { id: "sdr-4", seatX: 164, seatY: 552, standX: 164, standY: 590 },
      { id: "sdr-5", seatX: 268, seatY: 552, standX: 268, standY: 590 },

      { id: "cs-1", seatX: 496, seatY: 192, standX: 496, standY: 230 },
      { id: "cs-2", seatX: 568, seatY: 192, standX: 568, standY: 230 },
      { id: "cs-3", seatX: 640, seatY: 192, standX: 640, standY: 230 },

      { id: "product-1", seatX: 496, seatY: 432, standX: 496, standY: 470 },
      { id: "product-2", seatX: 568, seatY: 432, standX: 568, standY: 470 },
      { id: "product-3", seatX: 640, seatY: 432, standX: 640, standY: 470 },

      { id: "davi", seatX: 768, seatY: 192, standX: 768, standY: 230 },
      { id: "fabio", seatX: 944, seatY: 192, standX: 944, standY: 230 },
      { id: "diretoria", seatX: 1120, seatY: 192, standX: 1120, standY: 230 },

      { id: "hotdesk-1", seatX: 816, seatY: 656, standX: 816, standY: 694 },
    ];

    let spaceWorkActiveDesk: SpaceWorkDesk | null = null;
    let spaceWorkSeatedDesk: SpaceWorkDesk | null = null;

    const spaceWorkDeskOccupied = (
      desk: SpaceWorkDesk,
    ): boolean => {

      for (
        const [, sprite]
        of this.remotePlayersManager.getAllRemotes()
      ) {

        const distance =
          Phaser.Math.Distance.Between(
            sprite.x,
            sprite.y,
            desk.seatX,
            desk.seatY,
          );

        if (distance <= 22) {
          return true;
        }
      }

      return false;
    };

    const spaceWorkSit = (
      desk: SpaceWorkDesk,
    ) => {

      const hero =
        this.playerManager.getHero();

      this.playerManager
        .setDesiredPosition(null);

      this.playerManager
        .setMovementLocked(true);

      hero.setPosition(
        desk.seatX,
        desk.seatY,
      );

      try {
        hero.body.setVelocity(0, 0);
      } catch {}

      spaceWorkSeatedDesk = desk;
      spaceWorkActiveDesk = desk;
    };

    const spaceWorkStand = () => {

      if (!spaceWorkSeatedDesk) {
        return;
      }

      const desk =
        spaceWorkSeatedDesk;

      const hero =
        this.playerManager.getHero();

      hero.setPosition(
        desk.standX,
        desk.standY,
      );

      this.playerManager
        .setMovementLocked(false);

      spaceWorkSeatedDesk = null;
      spaceWorkActiveDesk = null;
    };


    const openSpaceWorkPanel = (
      id: SpaceWorkPanelId,
    ) => {
      window.dispatchEvent(
        new CustomEvent(
          "space-work:open-panel",
          {
            detail: { id },
          },
        ),
      );
    };

    const updateSpaceWorkInteraction =
      () => {

        if (
          this.editorMode ||
          this.mapRef?.width !== 80 ||
          this.mapRef?.height !== 52
        ) {

          spaceWorkActiveInteraction = null;
          spaceWorkActiveDesk = null;

          spaceWorkPromptBg.setVisible(false);
          spaceWorkPromptText.setVisible(false);

          return;
        }


        // Se ja esta sentado, E serve para levantar.
        if (spaceWorkSeatedDesk) {

          spaceWorkPromptText
            .setText("[E]  Levantar")
            .setVisible(true);

          spaceWorkPromptBg
            .setVisible(true);

          return;
        }


        const hero =
          this.playerManager.getHero();


        // ==========================================
        // MESA MAIS PROXIMA
        // ==========================================

        let nearestDesk:
          SpaceWorkDesk | null = null;

        let nearestDeskDistance =
          Number.POSITIVE_INFINITY;


        for (
          const desk
          of spaceWorkDesks
        ) {

          const distance =
            Phaser.Math.Distance.Between(
              hero.x,
              hero.y,
              desk.standX,
              desk.standY,
            );

          if (
            distance <
            nearestDeskDistance
          ) {

            nearestDeskDistance =
              distance;

            nearestDesk =
              desk;
          }
        }


        if (
          nearestDesk &&
          nearestDeskDistance <= 72
        ) {

          spaceWorkActiveInteraction =
            null;

          spaceWorkActiveDesk =
            nearestDesk;


          if (
            spaceWorkDeskOccupied(
              nearestDesk
            )
          ) {

            spaceWorkPromptText
              .setText("Mesa ocupada")
              .setVisible(true);

          } else {

            spaceWorkPromptText
              .setText("[E]  Sentar")
              .setVisible(true);
          }


          spaceWorkPromptBg
            .setVisible(true);

          return;
        }


        spaceWorkActiveDesk = null;


        // ==========================================
        // PAINEL MAIS PROXIMO
        // ==========================================

        let nearest:
          SpaceWorkInteraction | null =
            null;

        let nearestDistance =
          Number.POSITIVE_INFINITY;


        for (
          const interaction
          of spaceWorkInteractions
        ) {

          const distance =
            Phaser.Math.Distance.Between(
              hero.x,
              hero.y,
              interaction.x,
              interaction.y,
            );

          if (
            distance <
            nearestDistance
          ) {

            nearestDistance =
              distance;

            nearest =
              interaction;
          }
        }


        if (
          nearest &&
          nearestDistance <= 88
        ) {

          spaceWorkActiveInteraction =
            nearest;

          spaceWorkPromptText
            .setText(
              "[E]  Abrir " +
              nearest.label
            )
            .setVisible(true);

          spaceWorkPromptBg
            .setVisible(true);

          return;
        }


        spaceWorkActiveInteraction =
          null;

        spaceWorkPromptBg
          .setVisible(false);

        spaceWorkPromptText
          .setVisible(false);
      };


    spaceWorkInteractKey.on(
      "down",
      () => {

        if (
          spaceWorkSeatedDesk
        ) {

          spaceWorkStand();
          return;
        }


        if (
          spaceWorkActiveDesk
        ) {

          if (
            !spaceWorkDeskOccupied(
              spaceWorkActiveDesk
            )
          ) {

            spaceWorkSit(
              spaceWorkActiveDesk
            );
          }

          return;
        }


        if (
          spaceWorkActiveInteraction
        ) {

          openSpaceWorkPanel(
            spaceWorkActiveInteraction.id,
          );
        }
      },
    );

    const spaceWorkEscapeKey =
      this.input.keyboard!.addKey(
        Phaser.Input.Keyboard.KeyCodes.ESC,
      );

    spaceWorkEscapeKey.on(
      "down",
      () => {

        if (
          spaceWorkSeatedDesk
        ) {
          spaceWorkStand();
        }
      },
    );

    this.scale.on(
      "resize",
      (gameSize:
        Phaser.Structs.Size) => {

        spaceWorkPromptBg
          .setPosition(
            gameSize.width / 2,
            gameSize.height - 105,
          );

        spaceWorkPromptText
          .setPosition(
            gameSize.width / 2,
            gameSize.height - 105,
          );
      },
    );

    this.events.on(
      Phaser.Scenes.Events.UPDATE,
      updateSpaceWorkInteraction,
    );


    // ==================================================
    // SPACE WORK - STATUS AUTOMATICO LOCAL
    // ==================================================

    const spaceWorkStatusBg =
      this.add.rectangle(
        0,
        0,
        104,
        18,
        0x080c11,
        0.88,
      )
      .setStrokeStyle(
        1,
        0x303944,
        0.9,
      )
      .setDepth(9994)
      .setVisible(false);

    const spaceWorkStatusText =
      this.add.text(
        0,
        0,
        "🟢 Disponível",
        {
          fontFamily:
            "Arial, sans-serif",
          fontSize:
            "9px",
          color:
            "#ffffff",
          fontStyle:
            "bold",
        },
      )
      .setOrigin(0.5)
      .setDepth(9995)
      .setVisible(false);

    const spaceWorkStatusForZone =
      (zone: string | null) => {

        switch (zone) {

          case "Comercial":
          case "SDR / Pre-Vendas":
          case "Retencao / CS":
          case "Product / TI":
            return "💻 Trabalhando";

          case "Cafe":
            return "☕ No café";

          case "Lounge / Descompressao":
            return "🛋 No lounge";

          case "Sala de Reuniao":
            return "📅 Em reunião";

          case "Sala do Davi":
          case "Sala do Fabio":
          case "Diretoria":
            return "🔴 Em sala privada";

          default:
            return "🟢 Disponível";
        }
      };

    let spaceWorkCurrentStatus =
      "🟢 Disponível";

    const handleSpaceWorkZoneStatus =
      (event: Event) => {

        const detail =
          (
            event as CustomEvent<{
              zone?: string | null;
            }>
          ).detail;

        spaceWorkCurrentStatus =
          spaceWorkStatusForZone(
            detail?.zone ?? null,
          );

        spaceWorkStatusText
          .setText(
            spaceWorkCurrentStatus
          );

        const width =
          Math.max(
            92,
            spaceWorkStatusText.width + 18,
          );

        spaceWorkStatusBg
          .setSize(
            width,
            18,
          );

        spaceWorkStatusBg
          .setDisplaySize(
            width,
            18,
          );

        spaceWorkStatusBg
          .setVisible(true);

        spaceWorkStatusText
          .setVisible(true);
      };

    window.addEventListener(
      "space-work:zone-status",
      handleSpaceWorkZoneStatus,
    );

    this.events.on(
      Phaser.Scenes.Events.UPDATE,
      () => {

        if (
          !this.playerManager ||
          this.editorMode
        ) {
          spaceWorkStatusBg
            .setVisible(false);

          spaceWorkStatusText
            .setVisible(false);

          return;
        }

        const hero =
          this.playerManager.getHero();

        const y =
          hero.y + 31;

        spaceWorkStatusBg
          .setPosition(
            hero.x,
            y,
          );

        spaceWorkStatusText
          .setPosition(
            hero.x,
            y,
          );
      },
    );

    this.events.once(
      Phaser.Scenes.Events.SHUTDOWN,
      () => {
        window.removeEventListener(
          "space-work:zone-status",
          handleSpaceWorkZoneStatus,
        );
      },
    );

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.editorMode) {
        if (this.uiManager.getHoveredSprite()) this.uiManager.setHoveredSprite(null);
        this.uiManager.updateCursor(
          this.cameraController?.isPanning() || false,
          this.cameraController?.isSpaceHeld() || false,
        );
        return;
      }
      const worldPoint = pointer.positionToCamera(this.cameras.main) as Phaser.Math.Vector2;
      let foundHover = false;
      for (const [_id, sprite] of this.remotePlayersManager.getAllRemotes()) {
        if (sprite.getBounds().contains(worldPoint.x, worldPoint.y)) {
          if (this.uiManager.getHoveredSprite() !== sprite) this.uiManager.setHoveredSprite(sprite);
          foundHover = true;
          break;
        }
      }
      if (!foundHover && this.uiManager.getHoveredSprite()) this.uiManager.setHoveredSprite(null);
      this.uiManager.updateCursor(
        this.cameraController?.isPanning() || false,
        this.cameraController?.isSpaceHeld() || false,
      );
    });

    this.input.on(Phaser.Input.Events.POINTER_DOWN, (p: Phaser.Input.Pointer) => {
      if (this.editorMode) return;
      if (p.rightButtonDown()) {
        try {
          p.event?.preventDefault?.();
        } catch {}
        const worldPoint = p.positionToCamera(this.cameras.main) as Phaser.Math.Vector2;
        const evt = p.event as MouseEvent | undefined;

        // SPACE WORK: também permite abrir o card do próprio usuário.
        const hero = this.playerManager.getHero();

        if (
          hero &&
          hero.getBounds().contains(
            worldPoint.x,
            worldPoint.y,
          )
        ) {
          gameBridge.onRightClick({
            x: evt?.clientX ?? p.x,
            y: evt?.clientY ?? p.y,
            playerId: 'local',
          });

          return;
        }

        for (
          const [id, sprite]
          of this.remotePlayersManager.getAllRemotes()
        ) {
          if (
            sprite
              .getBounds()
              .contains(
                worldPoint.x,
                worldPoint.y,
              )
          ) {
            gameBridge.onRightClick({
              x: evt?.clientX ?? p.x,
              y: evt?.clientY ?? p.y,
              playerId: id,
            });

            break;
          }
        }
      }
    });

    try {
      this.input.mouse?.disableContextMenu?.();
    } catch {}
    this.game.canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      return false;
    });

    this.events.on(Phaser.Scenes.Events.UPDATE, () => {
      this.playerManager.update(this.input.keyboard!.createCursorKeys(), (data) => gameBridge.onLocalMove(data));
      const hero = this.playerManager.getHero();
      this.nameLabelManager.updateHeroLabel(hero.x, hero.y);
      this.nameLabelManager.updateAllRemoteLabels(this.remotePlayersManager.getAllRemotes());
      this.remotePlayersManager.update();
      this.cameraController?.autoFollowIfHeroOutOfView?.();
      updateRecenterUiVisibility(this);
      if (this.editorMode)
        this.cameraController.updateEditorPan(this.input.keyboard!.createCursorKeys(), this.game.loop.delta);
    });
  }

  private setupEditorSubscriptions() {
    EditorService.subscribe(() => {
      this.tileManager.updateBackgrounds();
      this.tileManager.updateGrid();
      this.uiManager.updateCursor(
        this.cameraController?.isPanning() || false,
        this.cameraController?.isSpaceHeld() || false,
      );
      // Spawn rendering is now handled by EditorRenderer via EditorIntegration
    });
  }

  private loadServerData() {
    setTimeout(() => {
      this.fetchAndApplyServerLayers()
        .then(() => {
          if (!this.editorMode)
            try {
              this.collisionLayer?.setVisible(false);
            } catch (e) {
              logger.error('[MainScene] Failed to hide collision layer', e);
            }
        })
        .catch((e) => logger.error('[MainScene] Failed to load server layers', e));
    }, 100);
    if (this.v2)
      try {
        this.collisionLayer?.setVisible(false);
      } catch (e) {
        logger.error('[MainScene] Failed to hide collision layer', e);
      }

    const pendingTilesets = window.pendingTilesets;
    if (pendingTilesets && Array.isArray(pendingTilesets)) {
      setTimeout(() => pendingTilesets.forEach((ts) => this.registerTileset(ts)), 100);
      window.pendingTilesets = undefined;
    }
  }

  private setupCleanup() {
    gameBridge.setSceneApi(this);
    window.currentPhaserScene = this as unknown as Window['currentPhaserScene'];
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.disposeAutotiles();
      try {
        this.editorIntegration?.destroy();
      } catch {}
      try {
        this.objectManager.destroy();
      } catch {}
      try {
        gameBridge.setSceneApi(null);
      } catch {}
    });
    this.events.once(Phaser.Scenes.Events.DESTROY, () => {
      this.disposeAutotiles();
      try {
        this.editorIntegration?.destroy();
      } catch {}
      try {
        this.objectManager.destroy();
      } catch {}
      try {
        gameBridge.setSceneApi(null);
      } catch {}
    });
  }

  recenterCamera() {
    this.cameraController.recenterCamera();
    updateRecenterUiVisibility(this);
  }

  setEditorMode(enabled: boolean) {
    this.editorMode = !!enabled;
    this.cameraController.setEditorMode(enabled);

    if (enabled) {
      this.uiManager.setHoveredSprite(null);
      this.captureEditorSnapshot();
      try {
        setCollisionVisible(this, true);
      } catch {}
      try {
        this.collisionLayer?.setVisible(false);
      } catch {}
      updateRecenterUiVisibility(this);
      this.nameLabelManager.setHeroLabelVisibility(false);
      this.nameLabelManager.setAllRemoteLabelsVisibility(false);
      try {
        this.bubbleOutlines.forEach((g) => g.setVisible(false));
      } catch {}
      try {
        this.objectManager.setAllSpritesVisible(false);
      } catch {}
      try {
        this.remotePlayersManager.setVisibility(false);
      } catch {}
      try {
        this.playerManager.setVisible(false);
      } catch {}
      void this.fetchAndApplyServerLayers().catch(() => {
        /* ignore: silent reload */
      });
    } else {
      this.nameLabelManager.setHeroLabelVisibility(true);
      this.nameLabelManager.setAllRemoteLabelsVisibility(true);
      try {
        this.bubbleOutlines.forEach((g) => g.setVisible(true));
      } catch {}
      try {
        this.objectManager.setAllSpritesVisible(true);
      } catch {}
      try {
        this.remotePlayersManager.setVisibility(true);
      } catch {}
      try {
        this.playerManager.setVisible(true);
      } catch {}
      this.uiManager.hideEditorOverlays();
      if (this.v2)
        try {
          this.collisionLayer?.setVisible(false);
        } catch {}
    }

    try {
      this.systems.forEach((s) => s.init());
      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        try {
          this.systems.forEach((s) => s.destroy());
        } catch {}
      });
    } catch {}
  }

  captureEditorSnapshot() {
    const currentObjects = EditorService.getState().mapObjects;
    this.editorMapObjectsSnapshot = JSON.parse(JSON.stringify(currentObjects)) as typeof currentObjects;
    logger.debug('[MainScene] Editor snapshot captured', { objectCount: currentObjects.length });
  }

  restoreEditorSnapshot() {
    if (!this.editorMapObjectsSnapshot) return;

    // Restore mapObjects in EditorService
    EditorService.dispatch({ type: 'LOAD_MAP_OBJECTS', objects: this.editorMapObjectsSnapshot });
    EditorService.dispatch({ type: 'CLEAR_PENDING_CHANGES' });

    // Force reload map from server to restore terrain/collision/walls state
    this.forceReloadMap();

    logger.debug('[MainScene] Editor snapshot restored', { objectCount: this.editorMapObjectsSnapshot.length });
    this.editorMapObjectsSnapshot = null;
  }

  syncRemotePlayers(
    players: Record<
      string,
      {
        x: number;
        y: number;
        direction: 'up' | 'down' | 'left' | 'right';
        prevX?: number;
        prevY?: number;
        name?: string | undefined;
        dnd?: boolean | undefined;
        avatarId?: string | undefined;
        isNpc?: boolean | undefined;
      }
    >,
  ) {
    const localSession: string | undefined = typeof window !== 'undefined' ? window.__localSessionId : undefined;
    const hero = this.playerManager.getHero();

    const filteredPlayers: typeof players = {};
    for (const [id, p] of Object.entries(players)) {
      if (localSession && id === localSession) continue;
      if (hero && Math.abs(p.x - hero.x) < 0.01 && Math.abs(p.y - hero.y) < 0.01) continue;
      filteredPlayers[id] = p;
    }

    this.remotePlayersManager.syncRemotePlayers(filteredPlayers, localSession);
    this.collisionManager.setRemotes(this.remotePlayersManager.getAllRemotes());

    for (const [id, p] of Object.entries(filteredPlayers)) {
      if (!this.nameLabelManager.getRemoteLabel(id)) {
        this.nameLabelManager.createRemoteLabel(id, p.name || `User ${id.substring(0, 6)}`, p.x, p.y, p.isNpc);
      }
      if (p && typeof p.name === 'string' && p.name) {
        this.nameLabelManager.updateRemoteLabelName(id, p.name);
      }
      if (p.dnd !== undefined) {
        this.nameLabelManager.setRemoteLabelAlpha(id, p.dnd ? 0.6 : 1);
      }
      this.nameLabelManager.updateRemoteLabel(id, p.x, p.y);
    }

    for (const id of Array.from(this.nameLabelManager.getAllRemoteLabels().keys())) {
      if (!filteredPlayers[id]) this.nameLabelManager.removeRemoteLabel(id);
    }
  }

  setDoNotDisturb(enabled: boolean) {
    this.playerManager.setTransparency(enabled ? 0.35 : 1);
    this.nameLabelManager.setHeroLabelAlpha(enabled ? 0.6 : 1);
    this.remotePlayersManager.setVisibility(true);
    this.nameLabelManager.setAllRemoteLabelsVisibility(true);
    this.bubbleOutlines.forEach((g) => g.setVisible(true));
  }

  setDesiredPosition(pos: { x: number; y: number } | null) {
    this.playerManager.setDesiredPosition(pos);
  }
  setMovementLocked(locked: boolean) {
    this.playerManager.setMovementLocked(locked);
  }
  findFreeSpotNear(targetId: string, options?: { radius?: number; step?: number }): { x: number; y: number } | null {
    return this.collisionManager.findFreeSpotNear(targetId, options);
  }
  setZoneOverlay(_polys: { name: string; points: unknown[] }[]) {
    /* Handled by EditorRenderer */
  }
  setZonesVisible(visible: boolean) {
    this.uiManager.setZonesVisible(visible);
  }
  setSpawnMarker(_pos: { x: number; y: number } | null) {
    /* Handled by EditorRenderer */
  }
  setAssetPreview(
    preview: {
      dataUrl: string;
      width?: number | undefined;
      height?: number | undefined;
      rotation?: number | undefined;
      packUuid?: string | undefined;
      itemId?: string | undefined;
    } | null,
  ) {
    if (this.editorIntegration) {
      this.editorIntegration.getRenderer().renderGhost(preview);
    } else {
      // Fall back to the old implementation
      setAssetPreview(this, preview);
    }
  }
  public getEditorRenderer() {
    return this.editorIntegration?.getRenderer() ?? null;
  }
  applyTerrainPaint(edit: {
    rect: { startX: number; startY: number; endX: number; endY: number };
    dataUrl: string;
    attempt?: number;
  }): void {
    void edit;
  }
  eraseTerrainRect(rect: { startX: number; startY: number; endX: number; endY: number }) {
    this.tileManager.eraseTerrainRect(rect);
  }
  paintTerrainRect(layer: string, rect: { x0: number; y0: number; x1: number; y1: number }, tileRefId: number) {
    this.tileManager?.paintTerrainRect(layer, rect, tileRefId);
  }
  applyWallPaint(edit: { rect: { startX: number; startY: number; endX: number; endY: number }; wallTypeId: number }) {
    if (!this.autotileGrid || !this.autotileRenderer) return;
    const { rect, wallTypeId } = edit;
    const x0 = Math.min(rect.startX, rect.endX);
    const y0 = Math.min(rect.startY, rect.endY);
    const x1 = Math.max(rect.startX, rect.endX);
    const y1 = Math.max(rect.startY, rect.endY);
    const affected: Array<{ x: number; y: number }> = [];
    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        if (wallTypeId > 0) {
          this.autotileGrid.set(tx, ty, wallTypeId);
        } else {
          this.autotileGrid.remove(tx, ty);
        }
        affected.push({ x: tx, y: ty });
        // Also include neighbors for bitmask recalculation
        affected.push({ x: tx, y: ty - 1 });
        affected.push({ x: tx + 1, y: ty });
        affected.push({ x: tx, y: ty + 1 });
        affected.push({ x: tx - 1, y: ty });
      }
    }
    this.autotileRenderer.updateArea(affected);
  }
  setSelectionRect(_rect: { x: number; y: number; w: number; h: number } | null) {
    // Handled by EditorRenderer via EditorIntegration
  }
  applyTilePaint(edit: {
    layer: 'EditorGround' | 'EditorWalls' | 'Collision';
    tilesetKey: string;
    tileIndex: number;
    rect: { startX: number; startY: number; endX: number; endY: number };
  }) {
    this.tileManager.applyTilePaint(edit, this.collisionVisible, () => {
      this.collisionManager.ensureCollisionCollider();
      this.collisionManager.rebuildStaticColliders();
      if (this.collisionVisible) updateCollisionOverlay(this);
    });
  }
  saveEditorLayersHard() {}
  reloadEditorLayers() {}
  async fetchAndApplyServerLayers() {
    await fetchAndApplyServerLayers(this);
  }
  registerTileset(ts: {
    key: string;
    dataUrl: string;
    tileWidth: number;
    tileHeight: number;
    margin?: number | undefined;
    spacing?: number | undefined;
  }) {
    registerTileset(this, ts);
  }
  setCollisionVisible(visible: boolean) {
    setCollisionVisible(this, visible);
  }
  setBubbleMembers(members: Set<string>) {
    uiSetBubbleMembers(this, members);
  }
  setHeroName(name: string) {
    this.nameLabelManager.setHeroName(name);
  }
  updateSpeakingStates(speakingIds: Set<string>) {
    this.nameLabelManager.updateSpeakingStates(speakingIds);
  }
  changeHeroAvatar(avatarId: string) {
    const textureKey = avatarRegistry.getTextureKey(avatarId);
    if (this.textures.exists(textureKey)) {
      this.playerManager.changeAvatar(avatarId);
      return;
    }
    // Custom avatars (Phase 2 editor) need their manifest resolved before the
    // texture can be preloaded; resolve first (deduped) and re-enter on arrival.
    if (!avatarRegistry.getManifest(avatarId)) {
      void avatarRegistry.ensureManifest(avatarId).then((manifest) => {
        if (manifest) this.changeHeroAvatar(avatarId);
      });
      return;
    }
    avatarRegistry.preloadAvatar(this, avatarId);
    this.load.once('complete', () => {
      this.playerManager.changeAvatar(avatarId);
    });
    this.load.start();
  }
  handleObjectsUpdated(data: ObjectsUpdatedPayload) {
    this.objectManager.handleObjectsUpdated(data);
  }
  setBackgroundColor(hex: string) {
    try {
      this.cameras.main.setBackgroundColor(hex);
    } catch {}
  }
  private async loadVisibleChunks(layerName: ChunkLayerName) {
    await loadVisibleChunks(this, layerName);
  }
  public applyChunkUpdates(
    layerName: ChunkLayerName,
    updates: Array<{ key: string; version: number; encoding: string; data: string }>,
  ) {
    applyChunkUpdates(this, layerName, updates);
  }

  override update(time: number, delta: number) {
    super.update(time, delta);
    try {
      this.systems.forEach((s) => s.update(time, delta));
    } catch {}
    if (this.v2) {
      const vw = this.cameras.main.worldView;
      const camSig = `${Math.floor(vw.x)}:${Math.floor(vw.y)}:${Math.floor(vw.width)}:${Math.floor(vw.height)}:${this.cameras.main.zoom.toFixed(2)}`;
      if (camSig !== this._lastCamSig) {
        this._lastCamSig = camSig;
        void this.loadVisibleChunks('ground');
        void this.loadVisibleChunks('walls');
        void this.loadVisibleChunks('collision');
        void this.loadVisibleChunks('walls_auto');
        void this.objectManager.loadVisibleChunks(this.cameras.main);
      }
    }
  }

  forceReloadMap() {
    if (this.v2) {
      this.loadedChunks.clear();
      this._lastCamSig = null;
      try {
        logger.debug('[MainScene] Forced full map reload (chunks cleared)');
      } catch {}
      void this.loadVisibleChunks('ground');
      void this.loadVisibleChunks('walls');
      void this.loadVisibleChunks('collision');
      void this.loadVisibleChunks('walls_auto');
      this.fetchAndApplyServerLayers().catch(() => {});
    } else {
      this.fetchAndApplyServerLayers().catch(() => {});
    }
  }

  rebuildStaticColliders() {
    this.collisionManager.rebuildStaticColliders();
  }
  ensureCollisionCollider() {
    this.collisionManager.ensureCollisionCollider();
  }
  ensureEditorLayers() {
    this.tileManager.ensureEditorLayers();
  }
  updateCollisionOverlay() {
    updateCollisionOverlay(this);
  }

  async waitForTilesetsReady(keys: string[], timeoutMs: number = 1500): Promise<void> {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      if (keys.every((key) => this.textures.exists(key))) return;
      await new Promise((r) => setTimeout(r, 50));
    }
  }

  public updateTilesetRegistry(registry: V2Tileset[]) {
    this.tileManager.updateTilesetRegistry(registry);
  }

  registerAutotileDefinitions(items: V2Autotile[]): void {
    this.autotileTextureLoader?.register(items);
  }

  private disposeAutotiles(): void {
    this.autotileTextureLoader?.destroy();
    delete this.autotileTextureLoader;
    this.autotileRenderer?.destroy();
    delete this.autotileRenderer;
  }
}
